import axios from "axios";
import FileDropzone from "../../components/FileDropzone";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API } from "./shared";

export function BriefDeliveryPanel({ brief, role, userId, onChanged }) {
  return (
    <DesignDeliveryPanel
      kind="brief"
      item={brief}
      itemId={brief.id}
      role={role}
      userId={userId}
      onChanged={onChanged}
      urlBase={`${API}/design/briefs/${brief.id}`}
      buyerIdKey="buyer_id"
      counterpartyIdKey="awarded_agency_id"
    />
  );
}

export function DesignJobDeliveryPanel({ dj, role, userId, onChanged }) {
  return (
    <DesignDeliveryPanel
      kind="design_job"
      item={dj}
      itemId={dj.id}
      role={role}
      userId={userId}
      onChanged={onChanged}
      urlBase={`${API}/design-jobs/${dj.id}`}
      buyerIdKey="buyer_id"
      counterpartyIdKey="assigned_agency_id"
    />
  );
}

export function DesignDeliveryPanel({ kind, item, itemId, role, userId, onChanged, urlBase, buyerIdKey, counterpartyIdKey }) {
  const [sources, setSources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeliver, setShowDeliver] = useState(false);
  const [showNda, setShowNda] = useState(false);
  const [showRate, setShowRate] = useState(false);
  const [rateNudge, setRateNudge] = useState(false);
  const isBuyer = role === "buyer" && item?.[buyerIdKey] === userId;
  const isAgency = role === "agency" && item?.[counterpartyIdKey] === userId;

  const load = async () => {
    try {
      const { data } = await axios.get(`${urlBase}/sources`);
      setSources(data);
    } catch { setSources(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [itemId]);

  const nda = sources?.nda || {};
  const buyerSigned = !!nda.buyer_accepted_at;
  const agencySigned = !!nda.agency_accepted_at;
  const delivered = sources?.delivered;
  const alreadyRated = !!sources?.agency_rating?.stars;
  const canRate = isBuyer && delivered && buyerSigned && agencySigned && !alreadyRated;
  const agencyName = sources?.awarded_agency_name || item?.awarded_agency_name || item?.assigned_agency_name || "the agency";

  // When buyer clicks "Download all (ZIP)" / "Copyright PDF" and hasn't rated yet,
  // nudge a rating prompt a beat later so the download starts smoothly.
  const nudgeRating = () => {
    if (!canRate) return;
    const key = `gpp_rate_nudged_${itemId}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    setTimeout(() => { setRateNudge(true); }, 1400);
  };

  const signNda = async () => {
    try {
      await axios.post(`${urlBase}/nda-accept`, { accept: true });
      toast.success("NDA signed");
      setShowNda(false);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sign NDA");
    }
  };

  if (loading) return null;
  // No agency assigned yet → nothing to show.
  if (!item?.[counterpartyIdKey] && role !== "admin") return null;

  return (
    <div data-testid="delivery-panel" style={{marginTop:20,border:"1px solid #E5E7EB",borderRadius:12,background:"#fff",padding:"16px 18px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,marginBottom:12}}>
        <div>
          <h3 style={{margin:0,fontSize:14,color:"#111827"}}>Final delivery · NDA &amp; copyright</h3>
          <p style={{margin:"4px 0 0",fontSize:11,color:"#6B7280"}}>Source files unlock for the buyer only after both parties sign the mutual NDA and the agency ships final artwork.</p>
        </div>
        <span data-testid="delivery-status" style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",padding:"4px 10px",borderRadius:999,background: delivered ? "#DCFCE7" : "#FEF3C7", color: delivered ? "#065F46" : "#92400E"}}>
          {delivered ? "Delivered" : "Awaiting delivery"}
        </span>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
        <div data-testid="nda-buyer-row" style={{border:"1px solid #E5E7EB",borderRadius:8,padding:"8px 10px",fontSize:12}}>
          <div style={{color:"#6B7280",fontSize:10,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3,fontWeight:700}}>Buyer NDA</div>
          {buyerSigned ? <span style={{color:"#065F46",fontWeight:600}}>✓ Signed · {new Date(nda.buyer_accepted_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}</span> : <span style={{color:"#92400E"}}>Pending</span>}
        </div>
        <div data-testid="nda-agency-row" style={{border:"1px solid #E5E7EB",borderRadius:8,padding:"8px 10px",fontSize:12}}>
          <div style={{color:"#6B7280",fontSize:10,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3,fontWeight:700}}>Agency NDA</div>
          {agencySigned ? <span style={{color:"#065F46",fontWeight:600}}>✓ Signed · {new Date(nda.agency_accepted_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}</span> : <span style={{color:"#92400E"}}>Pending</span>}
        </div>
      </div>

      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:delivered?12:0}}>
        {isBuyer && !buyerSigned && (
          <button data-testid="nda-sign-buyer" onClick={() => setShowNda(true)} style={{background:"#111827",color:"#FACC15",border:"none",padding:"8px 16px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>Review &amp; sign NDA</button>
        )}
        {isAgency && !agencySigned && (
          <button data-testid="nda-sign-agency" onClick={() => setShowNda(true)} style={{background:"#111827",color:"#FACC15",border:"none",padding:"8px 16px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>Review &amp; sign NDA</button>
        )}
        {isAgency && !delivered && (
          <button data-testid="deliver-open" onClick={() => setShowDeliver(true)} style={{background:"#059669",color:"#fff",border:"none",padding:"8px 16px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>Deliver final artwork</button>
        )}
      </div>

      {delivered && (
        <div style={{marginTop:4}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,marginBottom:8}}>
            <div style={{fontSize:11,color:"#6B7280",textTransform:"uppercase",letterSpacing:"0.08em",fontWeight:700}}>Delivered source files · {(sources.source_file_urls || []).length} item{(sources.source_file_urls || []).length === 1 ? "" : "s"}</div>
            {!sources.download_blocked_reason && (sources.source_file_urls || []).length > 0 && (
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <a data-testid="sources-zip" onClick={nudgeRating} href={`${urlBase}/sources.zip`} target="_blank" rel="noreferrer" style={{background:"#111827",color:"#FACC15",textDecoration:"none",padding:"6px 12px",borderRadius:6,fontSize:11,fontWeight:700}}>Download all (ZIP)</a>
                <a data-testid="sources-copyright-pdf" onClick={nudgeRating} href={`${urlBase}/copyright-certificate.pdf`} target="_blank" rel="noreferrer" style={{background:"#F3F4F6",color:"#111827",textDecoration:"none",padding:"6px 12px",borderRadius:6,fontSize:11,fontWeight:700,border:"1px solid #D1D5DB"}}>Copyright certificate (PDF)</a>
              </div>
            )}
          </div>
          {sources.download_blocked_reason ? (
            <div style={{background:"#FEF3C7",border:"1px solid #FDE68A",color:"#92400E",padding:"8px 12px",borderRadius:8,fontSize:12}} data-testid="sources-blocked">{sources.download_blocked_reason}</div>
          ) : (sources.source_file_urls || []).length === 0 ? (
            <p style={{fontSize:12,color:"#9CA3AF"}}>No files attached.</p>
          ) : (
            <ol data-testid="sources-list" style={{listStyle:"none",padding:0,margin:0,display:"grid",gap:6,counterReset:"src"}}>
              {sources.source_file_urls.map((u, i) => {
                const fname = (u || "").split("/").pop() || `file-${i+1}`;
                const ext = (fname.split(".").pop() || "").toUpperCase();
                return (
                  <li key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,padding:"10px 12px",border:"1px solid #E5E7EB",borderRadius:8,fontSize:12,background:"#F9FAFB"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0,flex:1}}>
                      <span style={{background:"#111827",color:"#FACC15",fontSize:10,fontWeight:800,padding:"3px 7px",borderRadius:999,minWidth:22,textAlign:"center"}}>{i+1}</span>
                      <span style={{display:"inline-block",fontSize:9,fontWeight:800,letterSpacing:"0.06em",color:"#6B7280",background:"#E5E7EB",padding:"2px 6px",borderRadius:4}}>{ext || "FILE"}</span>
                      <span style={{color:"#111827",fontFamily:"'DM Mono', monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{fname}</span>
                    </div>
                    <a href={u} onClick={nudgeRating} target="_blank" rel="noreferrer" data-testid={`source-download-${i}`} style={{color:"#2563EB",fontWeight:600,textDecoration:"none",flexShrink:0}}>Download →</a>
                  </li>
                );
              })}
            </ol>
          )}
          <p style={{marginTop:10,fontSize:11,color:"#6B7280"}}>Copyright transferred to buyer on {new Date(sources.copyright_transferred_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}.{sources.delivery_note ? ` Agency note: ${sources.delivery_note}` : ""}</p>

          {alreadyRated && (
            <div data-testid="agency-rating-card" style={{marginTop:12,padding:"10px 14px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{display:"flex",gap:2}} aria-label={`${sources.agency_rating.stars} out of 5 stars`}>
                  {[1,2,3,4,5].map(s => (
                    <span key={s} style={{fontSize:16,color: s <= sources.agency_rating.stars ? "#F59E0B" : "#E5E7EB",lineHeight:1}}>★</span>
                  ))}
                </div>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:"#92400E"}}>You rated {agencyName} {sources.agency_rating.stars}/5</div>
                  <div style={{fontSize:10,color:"#78350F"}}>Thanks — your feedback keeps the network sharp.</div>
                </div>
              </div>
            </div>
          )}

          {canRate && rateNudge && !showRate && (
            <div data-testid="agency-rating-nudge" style={{marginTop:12,padding:"12px 14px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"#92400E"}}>Enjoying what {agencyName} delivered?</div>
                <div style={{fontSize:11,color:"#78350F",marginTop:2}}>Spare 20 seconds to leave a rating — it builds their reputation and helps other buyers.</div>
              </div>
              <div style={{display:"flex",gap:6}}>
                <button data-testid="agency-rating-dismiss" type="button" onClick={() => setRateNudge(false)} style={{background:"transparent",border:"1px solid #FDE68A",color:"#92400E",padding:"7px 12px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer"}}>Not now</button>
                <button data-testid="agency-rating-open-nudge" type="button" onClick={() => { setRateNudge(false); setShowRate(true); }} style={{background:"#111827",color:"#FACC15",border:"none",padding:"7px 14px",borderRadius:8,fontSize:11,fontWeight:700,cursor:"pointer"}}>Rate agency ★</button>
              </div>
            </div>
          )}

          {canRate && !rateNudge && (
            <div style={{marginTop:12,display:"flex",justifyContent:"flex-end"}}>
              <button data-testid="agency-rating-open" type="button" onClick={() => setShowRate(true)} style={{background:"#FEF3C7",color:"#92400E",border:"1px solid #FDE68A",padding:"7px 14px",borderRadius:8,fontSize:11,fontWeight:700,cursor:"pointer"}}>Rate {agencyName} ★</button>
            </div>
          )}
        </div>
      )}

      {showNda && (
        <NdaSignModal
          role={isBuyer ? "buyer" : "agency"}
          onCancel={() => setShowNda(false)}
          onSign={signNda}
        />
      )}

      {showDeliver && (
        <DeliveryModal
          deliverUrl={`${urlBase}/deliver`}
          onCancel={() => setShowDeliver(false)}
          onDone={async () => { setShowDeliver(false); await load(); onChanged && onChanged(); }}
        />
      )}

      {showRate && (
        <AgencyRatingModal
          agencyName={agencyName}
          rateUrl={`${urlBase}/rate-agency`}
          onCancel={() => setShowRate(false)}
          onDone={async () => { setShowRate(false); await load(); onChanged && onChanged(); }}
        />
      )}
    </div>
  );
}


export function NdaSignModal({ role, onCancel, onSign }) {
  const [checked, setChecked] = useState(false);
  return (
    <div onClick={onCancel} data-testid="nda-modal" style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1200,padding:24}}>
      <div onClick={e => e.stopPropagation()} style={{background:"#fff",borderRadius:14,width:560,maxWidth:"96vw",padding:"24px 28px",maxHeight:"90vh",overflow:"auto"}}>
        <h3 style={{margin:"0 0 6px",fontFamily:"'Fraunces', serif",fontSize:22}}>Mutual Non-Disclosure Agreement</h3>
        <p style={{margin:"0 0 12px",fontSize:12,color:"#6B7280"}}>Between the Buyer and the Awarded Agency, routed through GoPrintPack.com.</p>
        <div style={{maxHeight:280,overflowY:"auto",padding:"12px 14px",background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:10,fontSize:12,color:"#374151",lineHeight:1.65}}>
          <p><b>1. Confidential information.</b> Buyer may share brand strategy, product roadmaps, launch windows, pricing, dielines, formulation notes and other proprietary material with the Agency to allow design delivery. Agency may share process documentation, creative drafts, revision notes and source files with the Buyer.</p>
          <p><b>2. Non-use.</b> Both parties agree to use the counterparty's confidential information solely for executing this specific design brief and not for any other project, pitch, or portfolio showcase without written consent.</p>
          <p><b>3. No off-platform contact.</b> Both parties agree to keep all communications on GoPrintPack. Sharing personal email, phone or external links in chat is a breach of this NDA and the platform policy.</p>
          <p><b>4. Copyright on delivery.</b> On delivery of the final artwork and clearance of escrow, the Agency assigns full copyright and reproduction rights of the delivered artwork to the Buyer. Agency retains the right to list the finished SKU name in a portfolio without revealing confidential brand/formulation data.</p>
          <p><b>5. Term.</b> Confidentiality obligations survive for 24 months from the date of brief delivery or termination.</p>
          <p><b>6. Jurisdiction.</b> Disputes are resolved per the GoPrintPack Terms of Service, courts at Kopargaon, District Ahilyanagar, Maharashtra, India.</p>
        </div>
        <label style={{display:"flex",alignItems:"center",gap:8,marginTop:14,fontSize:13,cursor:"pointer"}}>
          <input data-testid="nda-check" type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)}/>
          <span>I have read the NDA and accept its terms as the <b>{role === "buyer" ? "Buyer" : "Awarded Agency"}</b>.</span>
        </label>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:16}}>
          <button type="button" onClick={onCancel} style={{background:"#F3F4F6",color:"#374151",border:"1px solid #D1D5DB",padding:"9px 16px",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer"}}>Cancel</button>
          <button type="button" data-testid="nda-confirm" disabled={!checked} onClick={onSign} style={{background: checked ? "#111827" : "#9CA3AF", color:"#FACC15", border:"none", padding:"9px 20px", borderRadius:8, fontSize:13, fontWeight:700, cursor: checked ? "pointer" : "not-allowed"}}>Sign NDA</button>
        </div>
      </div>
    </div>
  );
}


export function DeliveryModal({ deliverUrl, onCancel, onDone }) {
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState("");
  const [copyright, setCopyright] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!copyright || files.length === 0) return;
    setBusy(true); setErr("");
    try {
      const urls = files.map(f => f.url || f.href || f.file_url || f).filter(Boolean);
      await axios.post(deliverUrl, {
        source_file_urls: urls,
        copyright_transferred: true,
        delivery_note: note.trim() || null,
      });
      toast.success("Final artwork delivered");
      onDone && onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not deliver final artwork");
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onCancel} data-testid="deliver-modal" style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1200,padding:24}}>
      <div onClick={e => e.stopPropagation()} style={{background:"#fff",borderRadius:14,width:620,maxWidth:"96vw",padding:"24px 28px",maxHeight:"90vh",overflow:"auto"}}>
        <h3 style={{margin:"0 0 6px",fontFamily:"'Fraunces', serif",fontSize:22}}>Deliver final artwork</h3>
        <p style={{margin:"0 0 14px",fontSize:12,color:"#6B7280"}}>Upload the high-resolution source files you are handing over to the buyer. On delivery, copyright transfers to the buyer and escrow is scheduled for release.</p>

        {err && <div style={{background:"#FEF2F2",border:"1px solid #FECACA",color:"#991B1B",padding:"8px 12px",borderRadius:8,fontSize:12,marginBottom:12}}>{err}</div>}

        <label style={{fontSize:11,color:"#6B7280",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase"}}>High-resolution source files</label>
        <div style={{marginTop:6,marginBottom:14}}>
          <FileDropzone
            testid="deliver-dropzone"
            multiple
            accept="application/pdf,image/*,.ai,.eps,.psd,.indd,.zip,.rar,.svg,.dxf,video/*"
            hint="PDF, images, AI, EPS, PSD, ZIP, or video — up to 10 MB each"
            value={files}
            onChange={setFiles}
          />
          {files.length > 0 && (
            <div data-testid="deliver-files-summary" style={{marginTop:10,padding:"8px 12px",background:"#F0FDF4",border:"1px solid #86EFAC",borderRadius:8,fontSize:12,color:"#065F46",fontWeight:600}}>
              {files.length} file{files.length > 1 ? "s" : ""} ready to deliver
            </div>
          )}
        </div>

        <label style={{fontSize:11,color:"#6B7280",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase"}}>Delivery note (optional)
          <textarea data-testid="deliver-note" value={note} onChange={e => setNote(e.target.value)} rows={3} maxLength={600} placeholder="Dieline specs, print profile, open-ended issues…" style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,marginTop:6,fontFamily:"inherit"}}/>
          <span style={{display:"block",marginTop:3,fontSize:10,color:"#9CA3AF"}}>Contact details (email, phone, URLs) will be auto-removed.</span>
        </label>

        <label style={{display:"flex",alignItems:"flex-start",gap:8,marginTop:16,fontSize:13,cursor:"pointer",padding:"10px 12px",background:"#F0FDF4",border:"1px solid #86EFAC",borderRadius:8}}>
          <input data-testid="deliver-copyright" type="checkbox" checked={copyright} onChange={e => setCopyright(e.target.checked)} style={{marginTop:3}}/>
          <span style={{color:"#065F46",fontSize:12,lineHeight:1.5}}>
            <b>Copyright transfer confirmation.</b> I confirm that on buyer approval of these source files and escrow release, full copyright and reproduction rights of the delivered artwork transfer irrevocably to the buyer per the signed NDA (Clause 4).
          </span>
        </label>

        <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:18}}>
          <button type="button" onClick={onCancel} disabled={busy} style={{background:"#F3F4F6",color:"#374151",border:"1px solid #D1D5DB",padding:"9px 16px",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer"}}>Cancel</button>
          <button
            type="button"
            data-testid="deliver-submit"
            onClick={submit}
            disabled={busy || !copyright || files.length === 0}
            style={{background: (busy || !copyright || files.length === 0) ? "#9CA3AF" : "#059669", color:"#fff", border:"none", padding:"9px 20px", borderRadius:8, fontSize:13, fontWeight:700, cursor: (busy || !copyright || files.length === 0) ? "not-allowed" : "pointer"}}
          >{busy ? "Delivering…" : "Deliver final artwork"}</button>
        </div>
      </div>
    </div>
  );
}


// Buyer-side rating prompt — fires after the buyer downloads delivered artwork.
// Four-dimensional star picker (overall + creativity + communication + turnaround)
// + 1200-char feedback. Posts to {urlBase}/rate-agency. Feedback is contact-scrubbed
// server-side (NDA Clause 3 — no off-platform contact).
export function AgencyRatingModal({ agencyName, rateUrl, onCancel, onDone }) {
  const [r, setR] = useState({ stars: 5, creativity: 5, communication: 5, turnaround: 5, feedback: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr("");
    try {
      await axios.post(rateUrl, r);
      toast.success(`Thanks for rating ${agencyName} ${r.stars}/5 ★`);
      onDone && onDone();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not save your rating");
    } finally { setBusy(false); }
  };

  const dims = [
    ["stars", "Overall rating"],
    ["creativity", "Creativity & originality"],
    ["communication", "Communication responsiveness"],
    ["turnaround", "Turnaround & deadline adherence"],
  ];

  return (
    <div onClick={onCancel} data-testid="agency-rating-modal" style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1200,padding:24}}>
      <div onClick={e => e.stopPropagation()} style={{background:"#fff",borderRadius:14,width:580,maxWidth:"96vw",padding:"24px 28px",maxHeight:"92vh",overflow:"auto"}}>
        <h3 style={{margin:"0 0 6px",fontFamily:"'Fraunces', serif",fontSize:22}}>Rate {agencyName}</h3>
        <p style={{margin:"0 0 16px",fontSize:12,color:"#6B7280"}}>Your rating updates the agency's Quality Score on their public profile. It only takes 20 seconds and makes the network sharper for every buyer who comes after you.</p>

        {err && <div style={{background:"#FEF2F2",border:"1px solid #FECACA",color:"#991B1B",padding:"8px 12px",borderRadius:8,fontSize:12,marginBottom:12}}>{err}</div>}

        <form onSubmit={submit}>
          <div style={{display:"grid",gap:12,marginBottom:14}}>
            {dims.map(([k, label]) => (
              <div key={k} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"10px 12px",border:"1px solid #E5E7EB",borderRadius:8,background:"#F9FAFB"}}>
                <label style={{fontSize:12,fontWeight:600,color:"#111827"}}>{label}</label>
                <div data-testid={`agency-rating-${k}`} style={{display:"flex",gap:4,alignItems:"center"}}>
                  {[1,2,3,4,5].map(s => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`agency-rating-${k}-${s}`}
                      onClick={() => setR({ ...r, [k]: s })}
                      aria-label={`${s} star${s > 1 ? "s" : ""}`}
                      style={{background:"transparent",border:"none",cursor:"pointer",padding:2,fontSize:22,lineHeight:1,color: r[k] >= s ? "#F59E0B" : "#D1D5DB"}}
                    >★</button>
                  ))}
                  <b style={{fontSize:11,color:"#111827",marginLeft:6,minWidth:24}}>{r[k]}/5</b>
                </div>
              </div>
            ))}
          </div>

          <label style={{display:"block",fontSize:11,color:"#6B7280",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>Feedback (optional)</label>
          <textarea
            data-testid="agency-rating-feedback"
            value={r.feedback}
            onChange={e => setR({ ...r, feedback: e.target.value.slice(0, 1200) })}
            rows={4}
            placeholder="What went well, what could improve, would you hire them again…"
            style={{width:"100%",padding:"10px 12px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,fontFamily:"inherit",resize:"vertical"}}
          />
          <div style={{display:"flex",justifyContent:"space-between",marginTop:3}}>
            <span style={{fontSize:10,color:"#9CA3AF"}}>Contact details (email, phone, URLs) will be auto-removed.</span>
            <span style={{fontSize:10,color:"#9CA3AF"}}>{r.feedback.length}/1200</span>
          </div>

          <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:18}}>
            <button data-testid="agency-rating-cancel" type="button" onClick={onCancel} disabled={busy} style={{background:"#F3F4F6",color:"#374151",border:"1px solid #D1D5DB",padding:"9px 16px",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer"}}>Not now</button>
            <button
              data-testid="agency-rating-submit"
              type="submit"
              disabled={busy}
              style={{background: busy ? "#9CA3AF" : "#111827", color:"#FACC15", border:"none", padding:"9px 20px", borderRadius:8, fontSize:13, fontWeight:700, cursor: busy ? "not-allowed" : "pointer"}}
            >{busy ? "Saving…" : "Submit rating ★"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
